import SkeletonBase from "newui/components/Skeleton";
import { usePanelMemberStatus } from "../../../../../hooks/usePanelScoringQueries";
import { MemberRow } from "./MemberRow";
import type { PanelMemberListMode } from "./types";

interface InitialPanelMemberListViewProps {
	evalId: number;
	mode?: PanelMemberListMode;
	rfxId: number;
}

export const InitialPanelMemberListView: React.FC<InitialPanelMemberListViewProps> = ({
	rfxId,
	evalId,
	mode = "initial",
}) => {
	const { data: members, isLoading, error } = usePanelMemberStatus(rfxId, evalId, mode);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="panel-member-list-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="panel-member-list-error">
				Failed to load panel members.
			</p>
		);
	}

	const isFinal = mode === "final";
	const activeMembers = (members ?? []).filter((m) => {
		if (m.is_removed) {
			return false;
		}
		if (isFinal && m.status === "declined") {
			return false;
		}
		return true;
	});

	if (activeMembers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="panel-member-list-empty"
			>
				No panel members have been added to this evaluation.
			</div>
		);
	}

	const submittedCount = activeMembers.filter((m) =>
		isFinal ? m.final_status === "submitted" : m.status === "submitted"
	).length;
	const hasNonLeadMembers = activeMembers.some((m) => m.role !== "rfxLead");

	return (
		<div data-testid="panel-member-list">
			<div className="mb-3 flex items-center justify-between">
				<h4 className="text-sm font-medium text-gray-700">
					Panel Members
				</h4>
				<span className="text-xs text-gray-500">
					{submittedCount} of {activeMembers.length} submitted
				</span>
			</div>
			<div className="w-full overflow-visible">
				<table className="w-full min-w-[600px] border-collapse text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
							<th className="px-3 py-3">Member</th>
							<th className="px-3 py-3">Role</th>
							<th className="px-3 py-3">Status</th>
							<th className="px-3 py-3">Progress</th>
							<th className="px-3 py-3">Submitted</th>
							{hasNonLeadMembers && (
								<th className="px-3 py-3 w-12">Actions</th>
							)}
						</tr>
					</thead>
					<tbody>
						{activeMembers.map((member) => (
							<MemberRow
								key={member.id}
								member={member}
								rfxId={rfxId}
								evalId={evalId}
								showActions={hasNonLeadMembers}
								mode={mode}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
